/**
 * Utilities for parsing and building routes for entity links.
 * Separated from React component for testability.
 */

/** Supported entity types for linking. */
export const SUPPORTED_ENTITY_TYPES = [
  'project',
  'customer',
  'scheduledEvent',
] as const;

export type SupportedEntityType = (typeof SUPPORTED_ENTITY_TYPES)[number];

export interface ParsedPayacaUri {
  app: string;
  entityType: string;
  publicId: string;
}

const PAYACA_URI_REGEX = /^payaca:\/\/([a-zA-Z]+)\/([a-zA-Z]+)\/(.+)$/;

/**
 * Parse a payaca:// URI into its components.
 * Returns null if the URI format is invalid.
 */
export function parsePayacaUri(uri: string): ParsedPayacaUri | null {
  const match = uri.match(PAYACA_URI_REGEX);
  if (!match) return null;

  const [, app, entityType, publicId] = match;

  return {
    app,
    entityType,
    publicId,
  };
}

/**
 * Check if an entity type is supported for linking.
 */
export function isSupportedEntityType(
  entityType: string
): entityType is SupportedEntityType {
  return SUPPORTED_ENTITY_TYPES.includes(entityType as SupportedEntityType);
}

/**
 * Build a route path for supported entity types.
 * Returns null for unsupported entity types.
 */
export function getRoute(
  entityType: SupportedEntityType,
  publicId: string
): string {
  switch (entityType) {
    case 'project':
      return `/projects/${publicId}`;
    case 'customer':
      return `/customers/${publicId}`;
    case 'scheduledEvent':
      return `/schedule/${publicId}`;
  }
}

/**
 * Check if a URL is an external link (http/https).
 */
export function isExternalUrl(url: string): boolean {
  return url.startsWith('http://') || url.startsWith('https://');
}
